<template>
  <component
    :is="inline ? 'form' : 'div'"
    ref="form"
    class="multi-store-options"
    :class="[
      `multi-store-options--${namespace}`,
      inline ? 'multi-store-options--inline' : 'multi-store-options--modal',
      { 'form-group': !inline },
    ]"
    :action="action"
    :method="inline ? 'post' : null"
  >
    <template v-if="inline">
      <input
        name="_method"
        type="hidden"
        value="put"
      >

      <input
        name="return_to"
        type="hidden"
        :value="returnTo"
      >
    </template>

    <div class="multi-store-options__country-container">
      <transition name="fade">
        <!-- eslint-disable-next-line vue/no-restricted-html-elements -->
        <img
          v-if="flag"
          :key="`flag-${currentCountryCode}`"
          class="multi-store-options__flag"
          alt=""
          role="presentation"
          :src="flag.src"
          :srcset="flag.srcset"
        >
      </transition>

      <label
        class="visually-hidden"
        :for="`${namespace}-country-selector`"
        v-text="$string('general.multi_store.options.country')"
      />

      <select
        :id="`${namespace}-country-selector`"
        ref="country"
        v-model="currentCountryCode"
        class="multi-store-options__country"
        :class="[
          inline ? 'text-heading-2xs' : 'text-body-s',
          { 'multi-store-options__country--no-flag': !flag },
        ]"
        name="country_code"
        @change.prevent="handleSelectChange"
      >
        <option
          v-for="(country) of countries"
          :key="country.code"
          :value="country.code"
          v-text="getCountrySelectName(country)"
        />
      </select>
    </div>

    <label
      v-if="languageSelector"
      class="visually-hidden"
      :for="`${namespace}-language-selector`"
      v-text="$string('general.multi_store.options.language')"
    />

    <select
      v-if="languageSelector"
      :id="`${namespace}-language-selector`"
      ref="language"
      v-model="currentLanguageCode"
      class="multi-store-options__language"
      :class="inline ? 'text-heading-2xs' : 'text-body-s'"
      name="language_code"
      @change.prevent="handleSelectChange"
    >
      <option
        v-for="(language) of languages"
        :key="language.code"
        :value="language.code"
        v-text="language.name"
      />
    </select>

    <input
      v-else
      name="language_code"
      type="hidden"
      :value="languages[0]?.code"
    >

    <!-- Hidden elements used to calculate select widths -->
    <span
      ref="countryText"
      class="multi-store-options__width-element"
      :class="inline ? 'text-heading-2xs' : 'text-body-s'"
      aria-hidden="true"
    />

    <span
      ref="languageText"
      class="multi-store-options__width-element"
      :class="inline ? 'text-heading-2xs' : 'text-body-s'"
      aria-hidden="true"
    />
  </component>
</template>

<script>

/**
 * Vue: Multi-store options (multi-store-options)
 * -----------------------------------------------------------------------------
 * Localization form selects with options.
 * - Select names have to those names as they're set by Shopify.
 *
 * @param {Boolean} [inline] - Inline layout for options.
 * @param {Boolean} [languageSelector] - Display language selector.
 * @param {String} namespace - Namespace of component.
 *
 */
import { mapActions, mapState } from 'vuex'

import { multiStoreAction, multiStoreReturnTo } from '~/helpers/general'

export default {
  name: 'MultiStoreOptions',

  props: {
    inline: {
      type: Boolean,
      default: false,
    },
    languageSelector: {
      type: Boolean,
      default: true,
    },
    namespace: {
      type: String,
      default: '',
      required: true,
    },
  },

  data() {
    return {
      currentCountryCode: '',
      currentLanguageCode: '',
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      current: (state) => state['multi-store'].current,
      selectedStore: (state) => state['multi-store'].selectedStore,
      suggestedStore: (state) => state['multi-store'].suggestedStore,
      supported: (state) => state['multi-store'].supported,
    }),

    /**
     * Compute form action based on current country code.
     * @returns {String}
     */
    action() {
      if (!this.inline) {
        return null
      }

      return multiStoreAction(this.active?.instance)
    },

    /**
     * Compute active value.
     * - This is selected if user has changed select value, or suggested if user
     *   hasn't updated anything yet.
     */
    active() {
      return this.selectedStore.country ? this.selectedStore : this.suggestedStore
    },

    /**
     * Compute countries array.
     * @returns {Array}
     */
    countries() {
      if (!Object.keys(this.supported).length) {
        return [this.current.country]
      }

      return Object.values(this.supported)
        .map((value) => value.country)
        .sort((a, b) => {
          if (a.name < b.name) {
            return -1
          }

          if (a.name > b.name) {
            return 1
          }

          return 0
        })
    },

    /**
     * Compute flag URL.
     * @returns {Object}
     */
    flag() {
      const code = this.currentCountryCode?.toLowerCase()

      if (!code) {
        return false
      }

      return {
        src: `https://flagcdn.com/w20/${code}.png`,
        srcset: `https://flagcdn.com/w40/${code}.png 2x`,
      }
    },

    /**
     * Compute language choices based on current country.
     * @returns {Array}
     */
    languages() {
      return this.supported[this.currentCountryCode]?.languages?.length
        ? this.supported[this.currentCountryCode].languages
        : [this.current.language]
    },

    /**
     * Compute default language based on current country.
     * @returns {Array}
     */
    defaultLanguage() {
      const currentInstance = this.supported[this.currentCountryCode]

      if (!currentInstance.defaultLanguage || (this.languages.length === 1)) {
        return this.languages[0]
      }

      const defaultLanguage = this.languages.find((language) => {
        return language.code === currentInstance.defaultLanguage.toLowerCase()
      })

      if (!defaultLanguage) {
        return this.languages[0]
      }

      return defaultLanguage
    },

    /**
     * Compute return to URL.
     * @returns {String}
     */
    returnTo() {
      if (!this.inline) {
        return null
      }

      return multiStoreReturnTo(this.active)
    },
  },

  watch: {

    /**
     * Watch current country code to update select width.
     * - Also tests to see if previously selected language exists in country.
     * @param {String} countryCode - New country code.
     */
    currentCountryCode(countryCode, oldCountryCode) {
      if (!countryCode) {
        return
      }

      let selectedLanguage

      if (oldCountryCode) {
        selectedLanguage = this.defaultLanguage
        this.currentLanguageCode = selectedLanguage.code
      } else {
        selectedLanguage = this.languages.find((language) => {
          return language.code === this.currentLanguageCode
        })

        if (!selectedLanguage) {
          selectedLanguage = this.defaultLanguage
          this.currentLanguageCode = selectedLanguage.code
        }
      }

      this.updateSelectWidth('country')

      if (this.supported[countryCode]) {
        this.setSelectedStore({
          country: this.supported[countryCode].country,
          instance: this.supported[countryCode].instance,
          language: selectedLanguage,
        })
      }
    },

    /**
     * Watch current language code to update select width.
     * @param {String} languageCode - New language code.
     */
    currentLanguageCode(languageCode) {
      if (this.inline) {
        return
      }

      const matchedLanguage = this.languages.find((language) => {
        return language.code === languageCode
      })

      this.updateSelectWidth('language')

      if (!this.supported[this.currentCountryCode]) {
        return
      }

      this.setSelectedStore({
        country: this.supported[this.currentCountryCode].country,
        instance: this.supported[this.currentCountryCode].instance,
        language: matchedLanguage || this.defaultLanguage,
      })
    },

    /**
     * Watch suggested store to udpate default selection.
     */
    suggestedStore() {
      this.setCurrentValues()
    },
  },

  mounted() {
    this.setCurrentValues()
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      setPreference: 'multi-store/setPreference',
      setSelectedStore: 'multi-store/setSelectedStore',
    }),

    /**
     * Set current values.
     */
    setCurrentValues() {
      if (this.currentCountryCode || this.currentLanguageCode) {
        return
      }

      /**
       * Inline options should show current store.
       */
      this.currentCountryCode = this.inline
        ? this.current.country?.code || cnvs.store.country
        : this.suggestedStore.country?.code

      this.currentLanguageCode = this.inline
        ? this.current.language?.code || cnvs.store.language
        : this.suggestedStore.language?.code
    },

    /**
     * Get country select option name.
     * @param {Object} country - Current country.
     * @returns {String}
     */
    getCountrySelectName(country) {
      const string = this.languageSelector
        ? 'general.multi_store.format.country'
        : 'general.multi_store.format.country_short'

      return this.$string(string, {
        replace: {
          country_code: country.code,
          country_name: country.name,
          currency_code: country.currency,
          currency_symbol: country.currencySymbol,
        },
      })
    },

    /**
     * Update select width to match current option.
     * @param {String} select - 'country' or 'language' select.
     */
    async updateSelectWidth(select) {
      await this.$nextTick()

      /**
       * Use text value of option in text element.
       * - Use this element to calculate select's width.
       * - Otherwise select is always the width of the widest option.
       */
      const element = this.$refs[select]
      const option = element.selectedOptions[0]

      if (!option) {
        return
      }

      const textRef = `${select}Text`
      const textElement = this.$refs[textRef]
      textElement.innerText = option?.innerText
      const width = textElement.offsetWidth + 1

      /**
       * Match padding from styles.
       * - Select dropdown spacing variables.
       * - If non-inline language then include start padding.
       * - If country then add flag spacing.
       */
      let padding = this.inline
        ? 'var(--icon-2xs) + var(--spacing-2xs)'
        : '10px + var(--icon-2xs) + var(--spacing-m)'

      if (select === 'language' && !this.inline) {
        padding += ' + var(--spacing-m)'
      }

      if (select === 'country') {
        padding += this.inline
          ? ' + var(--icon-s) + var(--spacing-s)'
          : ' + var(--spacing-m) + var(--icon-s) + var(--spacing-xs)'
      }

      const style = this.inline ? 'width' : 'minWidth'
      element.style[style] = `calc(${width}px + ${padding})`
    },

    /**
     * Handle select change.
     * - Redirect on change when inline.
     */
    async handleSelectChange() {
      if (!this.inline) {
        return
      }

      /**
       * Wait for watch processes to update language if it doesn't exist in the
       * new country choice.
       */
      await this.$nextTick()

      const form = this.$refs.form
      const formData = new FormData(form)

      const preferences = {
        country: formData.get('country_code'),
        language: formData.get('language_code'),
      }

      if (cnvs.storybook) {
        window.console.log('Submit form', preferences)
        return
      }

      this.setPreference(preferences)
      sessionStorage.setItem('multi-store-dismissed', JSON.stringify(preferences))

      /**
       * Wait for preferences to be set and action/returnTo computed props to
       * finish calculating.
       */
      await this.$nextTick()
      form.submit()
    },
  },
}
</script>

<style lang="scss">
@import './multi-store-options';
</style>
